import React, { useMemo, useState, useRef, useCallback } from 'react';
import { TaskStatus, TaskStatusColors, TaskStatusLabels, GanttRow, GanttChartProps, FlatRow } from '../../types';
import { getUserName } from '../../data/mockData';
import { GANTT_DIMENSIONS } from '../../constants';
import { useToggleSet } from '../../hooks';
import { exportGanttToPdf } from './ganttPdfExport';
import { createLogger } from '../../utils/logger';
import './GanttChart.css';

const logger = createLogger('GanttChart');

function flattenRows(rows: GanttRow[], expanded: Set<string>, statusFilter: TaskStatus | 'ALL'): FlatRow[] {
  const result: FlatRow[] = [];

  function traverse(row: GanttRow) {
    if (row.type === 'task') {
      if (statusFilter !== 'ALL' && row.status !== statusFilter) return;
      if (!row.startDate || !row.endDate) return;
      result.push({ row, childTaskCount: 1 });
      return;
    }

    // For groups, compute span and count (respecting status filter)
    const filteredChildren = filterChildren(row, statusFilter);
    if (!filteredChildren) return; // No matching tasks in this group

    const span = getGroupSpanFiltered(row, statusFilter);
    result.push({ row, spanStart: span.start, spanEnd: span.end, childTaskCount: span.count });

    if (expanded.has(row.id)) {
      (row.children || []).forEach(child => traverse(child));
    }
  }

  rows.forEach(r => traverse(r));
  return result;
}

function filterChildren(row: GanttRow, statusFilter: TaskStatus | 'ALL'): boolean {
  if (row.type === 'task') {
    if (!row.startDate || !row.endDate) return false;
    return statusFilter === 'ALL' || row.status === statusFilter;
  }
  return (row.children || []).some(child => filterChildren(child, statusFilter));
}

function getGroupSpanFiltered(row: GanttRow, statusFilter: TaskStatus | 'ALL'): { start: string | undefined; end: string | undefined; count: number } {
  if (row.type === 'task') {
    if (!row.startDate || !row.endDate) return { start: undefined, end: undefined, count: 0 };
    if (statusFilter !== 'ALL' && row.status !== statusFilter) return { start: undefined, end: undefined, count: 0 };
    return { start: row.startDate, end: row.endDate, count: 1 };
  }
  let earliest: string | undefined;
  let latest: string | undefined;
  let count = 0;
  (row.children || []).forEach(child => {
    const sub = getGroupSpanFiltered(child, statusFilter);
    count += sub.count;
    if (sub.start && (!earliest || sub.start < earliest)) earliest = sub.start;
    if (sub.end && (!latest || sub.end > latest)) latest = sub.end;
  });
  return { start: earliest, end: latest, count };
}

// ==================== Component ====================
export const GanttChart: React.FC<GanttChartProps> = ({
  rows,
  projectStartDate,
  projectEndDate,
  onTaskClick,
  maxRows = 100,
}) => {
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'ALL'>('ALL');
  const [zoneFilter, setZoneFilter] = useState<string>('ALL');
  const { set: expanded, toggle: toggleExpand, replace: replaceExpanded, clear: clearExpanded } = useToggleSet(
    rows.filter(r => r.type === 'zone').map(r => r.id)
  );

  // Filter rows by selected zone
  const filteredRows = useMemo(() => {
    if (zoneFilter === 'ALL') return rows;
    return rows.filter(r => r.id === zoneFilter);
  }, [rows, zoneFilter]);
  const [showAll, setShowAll] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const taskListRef = useRef<HTMLDivElement>(null);

  const expandAll = () => {
    const all: string[] = [];
    function collect(r: GanttRow) {
      if (r.type !== 'task') { all.push(r.id); (r.children || []).forEach(collect); }
    }
    filteredRows.forEach(collect);
    replaceExpanded(all);
  };

  const collapseAll = () => clearExpanded();

  const flatRows = useMemo(() => flattenRows(filteredRows, expanded, statusFilter), [filteredRows, expanded, statusFilter]);
  const visibleRows = showAll ? flatRows : flatRows.slice(0, maxRows);
  const totalTaskCount = useMemo(() => {
    let c = 0;
    function count(r: GanttRow) {
      if (r.type === 'task' && r.startDate && r.endDate) {
        if (statusFilter === 'ALL' || r.status === statusFilter) c++;
      }
      (r.children || []).forEach(count);
    }
    filteredRows.forEach(count);
    return c;
  }, [filteredRows, statusFilter]);

  // Timeline
  const timeline = useMemo(() => {
    let startStr = projectStartDate;
    let endStr = projectEndDate;

    // If project dates are missing, compute range from task rows
    if (!startStr || !endStr || isNaN(new Date(startStr).getTime()) || isNaN(new Date(endStr).getTime())) {
      let earliest = '';
      let latest = '';
      const scanDates = (r: GanttRow) => {
        if (r.type === 'task') {
          if (r.startDate && (!earliest || r.startDate < earliest)) earliest = r.startDate;
          if (r.endDate && (!latest || r.endDate > latest)) latest = r.endDate;
        }
        (r.children || []).forEach(scanDates);
      };
      filteredRows.forEach(scanDates);
      if (!startStr || isNaN(new Date(startStr).getTime())) startStr = earliest || new Date().toISOString().slice(0, 10);
      if (!endStr || isNaN(new Date(endStr).getTime())) endStr = latest || startStr;
    }

    const start = new Date(startStr + 'T00:00:00');
    const end = new Date(endStr + 'T00:00:00');
    const days: Date[] = [];
    const current = new Date(start);
    while (current <= end) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    const months: { label: string; days: number; startIndex: number }[] = [];
    let currentMonth = '';
    days.forEach((d, i) => {
      const monthLabel = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (monthLabel !== currentMonth) {
        months.push({ label: monthLabel, days: 1, startIndex: i });
        currentMonth = monthLabel;
      } else {
        months[months.length - 1].days++;
      }
    });
    return { start, end, days, months, totalDays: days.length };
  }, [projectStartDate, projectEndDate, filteredRows]);

  const { DAY_WIDTH: dayWidth, ROW_HEIGHT: rowHeight, GROUP_ROW_HEIGHT: groupRowHeight, HEADER_HEIGHT: headerHeight } = GANTT_DIMENSIONS;

  const getBarPosition = (startDate: string, endDate: string) => {
    const taskStart = new Date(startDate + 'T00:00:00');
    const taskEnd = new Date(endDate + 'T00:00:00');
    const startOffset = Math.max(0, Math.floor((taskStart.getTime() - timeline.start.getTime()) / 86400000));
    const endOffset = Math.min(timeline.totalDays - 1, Math.floor((taskEnd.getTime() - timeline.start.getTime()) / 86400000));
    const duration = Math.max(1, endOffset - startOffset + 1);
    return { left: startOffset * dayWidth, width: duration * dayWidth - 2 };
  };

  // Today marker
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayOffset = Math.floor((today.getTime() - timeline.start.getTime()) / 86400000);
  const showTodayMarker = todayOffset >= 0 && todayOffset < timeline.totalDays;

  // Scroll sync
  const handleTimelineScroll = () => {
    if (timelineRef.current && taskListRef.current) {
      taskListRef.current.scrollTop = timelineRef.current.scrollTop;
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (exporting) return;
    setExporting(true);
    // Small delay so the button state updates before the synchronous PDF work
    setTimeout(() => {
      try {
        exportGanttToPdf({
          rows: filteredRows,
          projectStartDate,
          projectEndDate,
          statusFilter,
          getUserName,
        });
      } catch (err) {
        logger.error('Error exporting Gantt chart:', err);
      } finally {
        setExporting(false);
      }
    }, 50);
  }, [exporting, filteredRows, projectStartDate, projectEndDate, statusFilter]);

  // Compute row tops
  const getRowHeight = (fr: FlatRow) => fr.row.type === 'task' ? rowHeight : groupRowHeight;

  const rowTops = useMemo(() => {
    const tops: number[] = [];
    let y = 0;
    visibleRows.forEach(fr => {
      tops.push(y);
      y += getRowHeight(fr);
    });
    return tops;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRows]);

  const totalHeight = rowTops.length > 0 ? rowTops[rowTops.length - 1] + getRowHeight(visibleRows[visibleRows.length - 1]) : 100;

  if (totalTaskCount === 0) {
    return <div className="gantt-empty"><p>No tasks with dates to display.</p></div>;
  }

  const indentClass = (level: number) => `gantt-indent-${level}`;

  return (
    <div className="gantt-container" id="gantt-export-area">
      {/* Controls */}
      <div className="gantt-controls">
        <select
          className="gantt-filter-select"
          value={zoneFilter}
          onChange={e => setZoneFilter(e.target.value)}
        >
          <option value="ALL">All Zones</option>
          {rows.filter(r => r.type === 'zone').map(z => (
            <option key={z.id} value={z.id}>{z.code} — {z.name}</option>
          ))}
        </select>
        <select
          className="gantt-filter-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as TaskStatus | 'ALL')}
        >
          <option value="ALL">All Statuses</option>
          {(Object.keys(TaskStatusLabels) as TaskStatus[]).map(s => (
            <option key={s} value={s}>{TaskStatusLabels[s]}</option>
          ))}
        </select>
        <span className="gantt-task-count">{totalTaskCount} tasks</span>
        <button className="gantt-expand-btn" onClick={expandAll}>Expand All</button>
        <button className="gantt-expand-btn" onClick={collapseAll}>Collapse All</button>
        <button className="gantt-export-btn" onClick={handleExport} disabled={exporting} title="Export Gantt Chart as PDF">
          {exporting ? 'Exporting…' : 'Export PDF'}
        </button>
      </div>

      <div className="gantt-chart">
        {/* Left panel */}
        <div className="gantt-task-list">
          <div className="gantt-task-list-header" style={{ height: headerHeight }}>
            <span>Task</span>
          </div>
          <div className="gantt-task-list-body" ref={taskListRef}>
            {visibleRows.map((fr, idx) => {
              const { row } = fr;
              const isGroup = row.type !== 'task';
              const isExpanded = expanded.has(row.id);
              const h = getRowHeight(fr);

              return (
                <div
                  key={row.id}
                  className={`gantt-task-row gantt-row-${row.type}`}
                  style={{ height: h }}
                  onClick={() => {
                    if (isGroup) toggleExpand(row.id);
                    else onTaskClick?.(row.id);
                  }}
                  title={isGroup ? `${row.code} — ${row.name} (${fr.childTaskCount} tasks)` : `${row.code} — ${row.name}`}
                >
                  <div className={`gantt-row-content ${indentClass(row.level)}`}>
                    {isGroup && (
                      <span className={`gantt-group-chevron ${isExpanded ? 'open' : ''}`}>›</span>
                    )}
                    <span className={isGroup ? 'gantt-group-code' : 'gantt-task-code'}>{row.code}</span>
                    <span className={isGroup ? 'gantt-group-name' : 'gantt-task-name'}>{row.name}</span>
                    {isGroup && <span className="gantt-group-count">({fr.childTaskCount})</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel: timeline */}
        <div className="gantt-timeline-wrapper" ref={timelineRef} onScroll={handleTimelineScroll}>
          <div className="gantt-timeline-header" style={{ height: headerHeight, width: timeline.totalDays * dayWidth }}>
            <div className="gantt-month-row">
              {timeline.months.map((m, i) => (
                <div key={i} className="gantt-month-cell" style={{ left: m.startIndex * dayWidth, width: m.days * dayWidth }}>
                  {m.label}
                </div>
              ))}
            </div>
            <div className="gantt-day-row">
              {timeline.days.map((d, i) => {
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <div key={i} className={`gantt-day-cell ${isWeekend ? 'weekend' : ''}`} style={{ left: i * dayWidth, width: dayWidth }}>
                    {d.getDate()}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="gantt-timeline-body" style={{ width: timeline.totalDays * dayWidth, height: totalHeight }}>
            {/* Weekend columns */}
            {timeline.days.map((d, i) => {
              if (d.getDay() !== 0 && d.getDay() !== 6) return null;
              return <div key={`bg-${i}`} className="gantt-weekend-col" style={{ left: i * dayWidth, width: dayWidth, height: totalHeight }} />;
            })}

            {/* Today marker */}
            {showTodayMarker && (
              <div className="gantt-today-marker" style={{ left: todayOffset * dayWidth + dayWidth / 2, height: totalHeight }} />
            )}

            {/* Bars */}
            {visibleRows.map((fr, idx) => {
              const { row } = fr;
              const h = getRowHeight(fr);
              const top = rowTops[idx];

              if (row.type === 'task' && row.startDate && row.endDate && row.status) {
                const pos = getBarPosition(row.startDate, row.endDate);
                const assigneeText = (row.assignedTo || []).map(id => getUserName(id).split(' ').map(n => n[0]).join('')).join(', ');
                return (
                  <div
                    key={row.id}
                    className="gantt-bar"
                    style={{
                      top: top + 4,
                      left: pos.left,
                      width: pos.width,
                      height: h - 8,
                      backgroundColor: TaskStatusColors[row.status],
                    }}
                    onClick={() => onTaskClick?.(row.id)}
                    title={`${row.code} — ${row.name}\n${row.startDate} to ${row.endDate}\nAssigned: ${(row.assignedTo || []).map(getUserName).join(', ')}`}
                  >
                    <span className="gantt-bar-text">{row.code}</span>
                    {pos.width > 60 && <span className="gantt-bar-assignee">{assigneeText}</span>}
                  </div>
                );
              }

              // Group span bar
              if (row.type !== 'task' && fr.spanStart && fr.spanEnd) {
                const pos = getBarPosition(fr.spanStart, fr.spanEnd);
                return (
                  <div
                    key={row.id}
                    className={`gantt-group-bar gantt-group-bar-${row.type}`}
                    style={{
                      top: top + Math.floor(h / 2) - 2,
                      left: pos.left,
                      width: pos.width,
                      height: 4,
                    }}
                  />
                );
              }

              return null;
            })}
          </div>
        </div>
      </div>

      {!showAll && flatRows.length > maxRows && (
        <button className="gantt-show-more" onClick={() => setShowAll(true)}>
          Show all ({flatRows.length - maxRows} more rows)
        </button>
      )}
    </div>
  );
};
